import type { LayerSpecification, Map as MlMap } from "maplibre-gl";
import type { OsmBackgroundOptions, OsmStyleName, TileLayerName } from "../types";

/**
 * Les quatre déclarent les mêmes `sources`, `glyphs` et `sprite` : c'est cet invariant qui permet de
 * changer de style en remplaçant le seul tableau de calques, sans `map.setStyle()` ni
 * `setSprite`/`setGlyphs`. Un style qui en dévierait s'afficherait sans ses icônes.
 */
export const OSM_STYLE_URLS: Record<OsmStyleName, string> = {
  light: "https://tiles.openfreemap.org/styles/positron",
  dark: "https://tiles.openfreemap.org/styles/dark",
  sober: "https://tiles.openfreemap.org/styles/fiord",
  detailed: "https://tiles.openfreemap.org/styles/liberty",
};

// Chargé à la construction de chaque instance MapLibre : il apporte les sources/polices/sprite
// communs à tous les autres, d'où le fait que `"light"` ne coûte aucune requête supplémentaire.
export const DEFAULT_STYLE = OSM_STYLE_URLS.light;

/**
 * Fond de carte satellite (voir `TileLayerName`) : tuiles raster Esri World Imagery, gratuites sans
 * clé d'API (contrairement à la plupart des fournisseurs d'imagerie satellite) — pas de style
 * vectoriel équivalent chez OpenFreeMap (qui ne fournit que des styles "plan", pas d'imagerie aérienne).
 */
export const SATELLITE_TILE_SOURCE_ID = "background-satellite";
export const SATELLITE_TILE_LAYER_ID = "background-satellite";
export const SATELLITE_TILE_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
export const SATELLITE_TILE_ATTRIBUTION = "Esri, Maxar, Earthstar Geographics";
/**
 * Dernier zoom où Esri a des images en France. Au-delà, il répond par une image « Map data not yet
 * available » plutôt que par une erreur : MapLibre l'afficherait. Borner la source lui fait agrandir
 * les tuiles de ce zoom à la place.
 */
export const SATELLITE_TILE_MAX_ZOOM = 19;

const DEFAULT_OSM_OPTIONS: Required<OsmBackgroundOptions> = {
  style: "light",
  language: "fr",
  placeLabels: true,
  roads: true,
  railways: true,
  tunnels: true,
  indoor: true,
  water: true,
  vegetation: true,
  roadLabels: true,
  poiLabels: true,
  buildings: true,
  buildings3d: false,
  fade: 0,
};

export type ResolvedOsmOptions = typeof DEFAULT_OSM_OPTIONS;

/** `language` se teste contre `undefined` et non par `??` : `null` y est une valeur significative
 * (garder le nom local), à ne pas confondre avec "champ non renseigné". */
export function resolveOsmOptions(options: OsmBackgroundOptions | undefined): ResolvedOsmOptions {
  return {
    style: options?.style ?? DEFAULT_OSM_OPTIONS.style,
    language: options?.language !== undefined ? options.language : DEFAULT_OSM_OPTIONS.language,
    placeLabels: options?.placeLabels ?? DEFAULT_OSM_OPTIONS.placeLabels,
    roads: options?.roads ?? DEFAULT_OSM_OPTIONS.roads,
    railways: options?.railways ?? DEFAULT_OSM_OPTIONS.railways,
    tunnels: options?.tunnels ?? DEFAULT_OSM_OPTIONS.tunnels,
    indoor: options?.indoor ?? DEFAULT_OSM_OPTIONS.indoor,
    water: options?.water ?? DEFAULT_OSM_OPTIONS.water,
    vegetation: options?.vegetation ?? DEFAULT_OSM_OPTIONS.vegetation,
    roadLabels: options?.roadLabels ?? DEFAULT_OSM_OPTIONS.roadLabels,
    poiLabels: options?.poiLabels ?? DEFAULT_OSM_OPTIONS.poiLabels,
    buildings: options?.buildings ?? DEFAULT_OSM_OPTIONS.buildings,
    buildings3d: options?.buildings3d ?? DEFAULT_OSM_OPTIONS.buildings3d,
    fade: clampFade(options?.fade ?? DEFAULT_OSM_OPTIONS.fade),
  };
}

function clampFade(fade: number): number {
  return Number.isFinite(fade) ? Math.min(1, Math.max(0, fade)) : DEFAULT_OSM_OPTIONS.fade;
}

/** La PROMESSE est mémorisée, pas son résultat : deux appels rapprochés partagent un seul `fetch`.
 * Une promesse rompue est retirée, laissant un appel ultérieur réessayer. */
const osmStyleLayersCache = new Map<OsmStyleName, Promise<LayerSpecification[]>>();

/** Systématique à la sortie du cache, partagé par toutes les cartes : un `LayerSpecification` remis
 * à `map.addLayer()` appartient ensuite à cette carte-là. */
function cloneLayers(layers: LayerSpecification[]): LayerSpecification[] {
  return JSON.parse(JSON.stringify(layers)) as LayerSpecification[];
}

export function primeOsmStyleLayers(style: OsmStyleName, layers: LayerSpecification[]): void {
  if (!osmStyleLayersCache.has(style)) osmStyleLayersCache.set(style, Promise.resolve(cloneLayers(layers)));
}

/** En copie propre à l'appelant. Rejette si le style est injoignable. */
export function fetchOsmStyleLayers(style: OsmStyleName): Promise<LayerSpecification[]> {
  const cached = osmStyleLayersCache.get(style);
  if (cached) return cached.then(cloneLayers);

  const pending = fetch(OSM_STYLE_URLS[style])
    .then((response) => {
      if (!response.ok) throw new Error(`Style OSM "${style}" injoignable (HTTP ${response.status})`);
      return response.json() as Promise<{ layers?: LayerSpecification[] }>;
    })
    .then((json) => json.layers ?? [])
    .catch((error) => {
      osmStyleLayersCache.delete(style);
      throw error;
    });

  osmStyleLayersCache.set(style, pending);
  return pending.then(cloneLayers);
}

/**
 * Classement par `source-layer` et jamais par id de calque : les ids diffèrent d'un style à l'autre
 * (`label_city` ici, `place_city` là) alors que le schéma OpenMapTiles leur est commun.
 *
 * `transportation` est rangé avec les noms de rues bien qu'il ne porte pas de texte : ce sont les
 * flèches de sens unique, qui n'ont pas à survivre seules au masquage des noms de voies.
 */
const SYMBOL_SOURCE_LAYERS: Record<"place" | "road" | "poi", readonly string[]> = {
  place: ["place", "water_name", "waterway", "aerodrome_label"],
  road: ["transportation_name", "transportation"],
  poi: ["poi"],
};

const ROAD_SOURCE_LAYERS: readonly string[] = ["transportation", "transportation_name"];

const WATER_SOURCE_LAYERS: readonly string[] = ["water", "waterway", "water_name"];

function sourceLayerOf(layer: LayerSpecification): string | undefined {
  return (layer as { "source-layer"?: string })["source-layer"];
}

/** Tous types de calques confondus : tracés, ponts, tunnels, flèches de sens unique et noms. */
function isRoadLayer(layer: LayerSpecification): boolean {
  const sourceLayer = sourceLayerOf(layer);
  return sourceLayer !== undefined && ROAD_SOURCE_LAYERS.includes(sourceLayer);
}

/**
 * Vrai si `filter` n'admet que des objets dont `key` prend l'une de `values` — au sens des deux
 * écritures des styles OpenMapTiles, `["==", ["get", key], v]` et
 * `["match", ["get", key], [v…], true, false]`, éventuellement sous un `"all"`. Un filtre qui
 * EXCLUT ces valeurs (`!=`, `match … false, true`) ne compte pas.
 */
function filterRequires(filter: unknown, key: string, values: readonly string[]): boolean {
  if (!Array.isArray(filter)) return false;
  const [op, subject, operand, whenMatched, otherwise] = filter as unknown[];
  const isKey = (value: unknown) => value === key || (Array.isArray(value) && value[0] === "get" && value[1] === key);

  if (op === "all") return filter.slice(1).some((part) => filterRequires(part, key, values));
  if (op === "==" && isKey(subject)) return typeof operand === "string" && values.includes(operand);
  if (op === "in" && subject === key) return filter.slice(2).some((value) => typeof value === "string" && values.includes(value));
  if (op === "match" && isKey(subject) && whenMatched === true && otherwise === false) {
    const listed = Array.isArray(operand) ? operand : [operand];
    return listed.some((value) => typeof value === "string" && values.includes(value));
  }
  return false;
}

function layerFilter(layer: LayerSpecification): unknown {
  return (layer as { filter?: unknown }).filter;
}

function isRailwayLayer(layer: LayerSpecification): boolean {
  return isRoadLayer(layer) && filterRequires(layerFilter(layer), "class", ["rail", "transit"]);
}

function isTunnelLayer(layer: LayerSpecification): boolean {
  return isRoadLayer(layer) && filterRequires(layerFilter(layer), "brunnel", ["tunnel"]);
}

/** Les cheminements intérieurs (`indoor = 1`) se mêlent aux voies extérieures dans les mêmes calques : on les filtre objet par objet. */
function withoutIndoor(layer: LayerSpecification): LayerSpecification {
  if (!isRoadLayer(layer)) return layer;
  const outdoor: unknown = ["!=", ["coalesce", ["get", "indoor"], 0], 1];
  const filter = layerFilter(layer);
  return { ...layer, filter: (filter ? ["all", filter, outdoor] : outdoor) } as LayerSpecification;
}

function isWaterLayer(layer: LayerSpecification): boolean {
  const sourceLayer = sourceLayerOf(layer);
  return sourceLayer !== undefined && WATER_SOURCE_LAYERS.includes(sourceLayer);
}

/** Le style sombre range ses parcs dans `landuse`, que seul son filtre distingue des quartiers. */
function isVegetationLayer(layer: LayerSpecification): boolean {
  const sourceLayer = sourceLayerOf(layer);
  if (sourceLayer === "park" || sourceLayer === "landcover") return true;
  if (sourceLayer !== "landuse") return false;

  const filter = (layer as { filter?: unknown }).filter;
  return JSON.stringify(filter ?? null).includes('"park"');
}

/** Les plans et les cours d'eau, sans leurs noms. */
export function waterLayers(layers: LayerSpecification[]): LayerSpecification[] {
  return layers.filter((layer) => layer.type !== "symbol" && ["water", "waterway"].includes(sourceLayerOf(layer) ?? ""));
}

const GROUND_SURFACE_SOURCE_LAYERS: readonly string[] = ["park", "landcover", "landuse", "aeroway", "transportation"];

/** Les surfaces du sol (végétation, sable, aéroports, zones piétonnes hachurées…), par opposition à l'eau, au bâti et aux traits du réseau. */
export function groundSurfaceLayers(layers: LayerSpecification[]): LayerSpecification[] {
  return layers.filter((layer) => layer.type === "fill" && GROUND_SURFACE_SOURCE_LAYERS.includes(sourceLayerOf(layer) ?? ""));
}

/** `undefined` pour un calque non-`symbol` comme pour un `source-layer` inconnu, qui reste donc
 * toujours affiché : on ne masque jamais ce qu'on n'a pas su reconnaître. */
export function symbolLayerCategory(layer: LayerSpecification): "place" | "road" | "poi" | undefined {
  if (layer.type !== "symbol") return undefined;
  const sourceLayer = sourceLayerOf(layer);
  if (!sourceLayer) return undefined;
  for (const [category, sourceLayers] of Object.entries(SYMBOL_SOURCE_LAYERS)) {
    if (sourceLayers.includes(sourceLayer)) return category as "place" | "road" | "poi";
  }
  return undefined;
}

/** Vrai si l'expression lit un champ de nom — par opposition aux écussons routiers, qui lisent `ref`. */
function readsNameField(expression: unknown): boolean {
  if (!Array.isArray(expression)) return false;
  if (expression[0] === "get" && typeof expression[1] === "string" && expression[1].startsWith("name")) return true;
  return expression.some(readsNameField);
}

/**
 * Passe les libellés dans `language` (code OSM, voir `OsmBackgroundOptions.language`), en préfixant
 * leur `text-field` d'un nom localisé plutôt qu'en le remplaçant : l'expression d'origine reste le
 * repli, ce qui préserve son traitement des écritures non latines (concaténation latin + non latin)
 * là où OSM n'a pas de nom dans cette langue.
 *
 * Les calques sans champ de nom sont laissés tels quels : un écusson routier affiche `ref`, et lui
 * ajouter un repli localisé y ferait apparaître le nom de la rue à la place du numéro de route.
 *
 * `language` à `null`/`undefined` : aucune traduction imposée, les calques ressortent intacts.
 */
export function localizeStyleLayers(
  layers: LayerSpecification[],
  language: string | null | undefined,
): LayerSpecification[] {
  if (!language) return layers;

  return layers.map((layer) => {
    if (layer.type !== "symbol") return layer;

    const textField = layer.layout?.["text-field"];
    if (!readsNameField(textField)) return layer;

    return {
      ...layer,
      layout: {
        ...layer.layout,
        "text-field": ["coalesce", ["get", `name:${language}`], textField],
      },
    } as LayerSpecification;
  });
}

export const OSM_BUILDING_3D_LAYER_ID = "background-osm-building-3d";

export const OSM_FADE_LAYER_ID = "background-osm-fade";

const FADE_FALLBACK_COLOR = "#ffffff";

/** Le `source-layer` `building` n'est servi qu'à partir de 13/14 selon le style. */
const BUILDING_3D_MIN_ZOOM = 14;

const BUILDING_3D_FALLBACK_COLOR = "hsl(35,8%,85%)";

const BUILDING_3D_OPACITY = 0.8;

/**
 * Construit ici plutôt qu'emprunté au style, que tous ne fournissent pas. Sa couleur est lue sur le
 * calque `building` du style en cours, pour s'y accorder sans table de correspondance.
 */
export function buildingExtrusionLayer(
  layers: LayerSpecification[],
): Extract<LayerSpecification, { type: "fill-extrusion" }> | null {
  const buildingLayer = layers.find(
    (layer): layer is Extract<LayerSpecification, { type: "fill" }> =>
      layer.type === "fill" && layer["source-layer"] === "building",
  );
  if (!buildingLayer) return null;

  const extrusion: Extract<LayerSpecification, { type: "fill-extrusion" }> = {
    id: OSM_BUILDING_3D_LAYER_ID,
    type: "fill-extrusion",
    source: buildingLayer.source,
    "source-layer": "building",
    minzoom: BUILDING_3D_MIN_ZOOM,
    paint: {
      "fill-extrusion-color": buildingLayer.paint?.["fill-color"] ?? BUILDING_3D_FALLBACK_COLOR,
      "fill-extrusion-height": ["get", "render_height"],
      "fill-extrusion-base": ["get", "render_min_height"],
      // Légère transparence : une façade opaque écrase les formes dessinées qu'elle recouvre en vue
      // inclinée, alors que le fond doit rester un décor. Même valeur que le `building-3d` de liberty.
      "fill-extrusion-opacity": BUILDING_3D_OPACITY,
    },
  };

  return extrusion;
}

/**
 * Rend le tableau prêt pour `addBackgroundTileLayer`. Masquage par retrait plutôt que par
 * `visibility: "none"` : un calque jamais ajouté ne coûte rien au moteur.
 */
/**
 * `revealBuildings` montre le bâti quels que soient `buildings` et `fade`, au-dessus du voile : la
 * baguette doit voir ce qu'elle reprend. Ce n'est pas un réglage de la carte, rien ne l'enregistre.
 */
export function applyOsmOptions(
  rawLayers: LayerSpecification[],
  options: OsmBackgroundOptions | undefined,
  revealBuildings = false,
): LayerSpecification[] {
  const resolved = resolveOsmOptions(options);
  const visible: Record<"place" | "road" | "poi", boolean> = {
    place: resolved.placeLabels,
    road: resolved.roadLabels,
    poi: resolved.poiLabels,
  };

  const kept = rawLayers.filter((layer) => {
    if (!resolved.roads && isRoadLayer(layer)) return false;
    if (!resolved.railways && isRailwayLayer(layer)) return false;
    if (!resolved.tunnels && isTunnelLayer(layer)) return false;
    if (!resolved.water && isWaterLayer(layer)) return false;
    if (!resolved.vegetation && isVegetationLayer(layer)) return false;
    const category = symbolLayerCategory(layer);
    return category === undefined || visible[category];
  });

  const layers = localizeStyleLayers(resolved.indoor ? kept : kept.map(withoutIndoor), resolved.language).map(solidFootpath);

  const painted = resolved.buildings || revealBuildings ? layers : layers.map(transparentBuilding);
  const fade = resolved.fade > 0 ? [fadeLayer(rawLayers, resolved.fade)] : [];
  const ordered = revealBuildings
    ? [...painted.filter((layer) => !isBuildingLayer(layer)), ...fade, ...painted.filter(isBuildingLayer)]
    : [...painted, ...fade];

  if (!resolved.buildings || !resolved.buildings3d) return ordered;

  const extrusion = buildingExtrusionLayer(rawLayers);
  if (!extrusion) return ordered;

  return [...ordered, withExtrusionOpacity(extrusion, BUILDING_3D_OPACITY * (1 - resolved.fade))];
}

function isBuildingLayer(layer: LayerSpecification): boolean {
  return sourceLayerOf(layer) === "building";
}

const FOOTPATH_OPACITY = 0.7;

/**
 * Les allées piétonnes en trait plein, un peu estompé pour les distinguer des routes : en
 * pointillés, elles se lisaient comme un tracé d'itinéraire.
 */
export function solidFootpath(layer: LayerSpecification): LayerSpecification {
  if (layer.type !== "line" || sourceLayerOf(layer) !== "transportation" || !layer.paint?.["line-dasharray"]) return layer;
  if (!JSON.stringify((layer as { filter?: unknown }).filter ?? null).includes('"path"')) return layer;

  const { "line-dasharray": _dashes, ...paint } = layer.paint;
  return { ...layer, paint: { ...paint, "line-opacity": paint["line-opacity"] ?? FOOTPATH_OPACITY } };
}

function transparentBuilding(layer: LayerSpecification): LayerSpecification {
  if (!isBuildingLayer(layer)) return layer;
  if (layer.type === "fill") return { ...layer, paint: { ...layer.paint, "fill-opacity": 0 } };
  if (layer.type === "fill-extrusion") return withExtrusionOpacity(layer, 0);
  return layer;
}

function withExtrusionOpacity(
  layer: Extract<LayerSpecification, { type: "fill-extrusion" }>,
  opacity: number,
): LayerSpecification {
  return { ...layer, paint: { ...layer.paint, "fill-extrusion-opacity": opacity } };
}

/**
 * Le relief est rangé au-dessus de ce voile par `MapLibreAdapter.placeBuildingLayer`, d'où son
 * opacité réduite d'autant à la place.
 */
function fadeLayer(rawLayers: LayerSpecification[], fade: number): LayerSpecification {
  const background = rawLayers.find(
    (layer): layer is Extract<LayerSpecification, { type: "background" }> => layer.type === "background",
  );

  return {
    id: OSM_FADE_LAYER_ID,
    type: "background",
    paint: {
      "background-color": background?.paint?.["background-color"] ?? FADE_FALLBACK_COLOR,
      "background-opacity": fade,
    },
  };
}

/**
 * Capture les calques du style vectoriel `DEFAULT_STYLE` juste chargé sur `map`, puis les retire —
 * factorisé entre `MapLibreAdapter.init()` (carte principale) et `BackgroundMap` (carte de fond
 * secondaire) : ce cycle "charger DEFAULT_STYLE → capturer ses layers → les retirer" doit être rejoué
 * à l'identique sur CHAQUE instance, MapLibre ne peignant que ce que contient le style de LA carte
 * où on l'ajoute.
 *
 * Rend les calques BRUTS : c'est `applyOsmOptions` qui en dérive ce qui sera peint. Ils alimentent
 * au passage le cache de styles, le style clair étant déjà téléchargé à ce stade.
 */
export function captureAndStripStyleLayers(map: MlMap): LayerSpecification[] {
  const layers = map.getStyle().layers ?? [];
  for (const layer of layers) {
    map.removeLayer(layer.id);
  }
  primeOsmStyleLayers("light", layers);
  return layers;
}

/** Retire le fond `name` de `map` — `styleLayers` = le tableau EXACTEMENT tel qu'il a été passé à
 * `addBackgroundTileLayer` (voir `MapLibreAdapter.paintedOsmLayers` : le tableau peint dépend des
 * `OsmBackgroundOptions`, en repasser un autre laisserait des calques orphelins). No-op pour
 * `"unset"` (rien n'est jamais affiché). */
export function removeBackgroundTileLayer(map: MlMap, name: TileLayerName, styleLayers: LayerSpecification[]): void {
  switch (name) {
    case "osm":
      for (const layer of styleLayers) {
        if (map.getLayer(layer.id)) map.removeLayer(layer.id);
      }
      break;
    case "satellite":
      if (map.getLayer(SATELLITE_TILE_LAYER_ID)) map.removeLayer(SATELLITE_TILE_LAYER_ID);
      if (map.getSource(SATELLITE_TILE_SOURCE_ID)) map.removeSource(SATELLITE_TILE_SOURCE_ID);
      break;
    case "unset":
      break;
  }
}

/**
 * Affiche le fond `name` sur `map` — `"osm"` ajoute les calques de `styleLayers` (ceux qu'a produits
 * `applyOsmOptions`), `"satellite"` ajoute une source/un calque raster (Esri World Imagery, voir
 * `SATELLITE_TILE_URL`). `beforeId`, si fourni, insère le fond sous ce calque plutôt qu'au sommet —
 * utile quand `map` porte déjà d'autres calques à garder au-dessus (voir
 * `MapLibreAdapter.getBottomLayerId`) ; sans objet sur une carte n'ayant vocation à ne jamais
 * recevoir d'autre calque (`BackgroundMap`). No-op pour `"unset"`.
 */
export function addBackgroundTileLayer(
  map: MlMap,
  name: TileLayerName,
  styleLayers: LayerSpecification[],
  beforeId?: string,
): void {
  switch (name) {
    case "osm":
      for (const layer of styleLayers) {
        map.addLayer(layer, beforeId);
      }
      break;
    case "satellite":
      map.addSource(SATELLITE_TILE_SOURCE_ID, {
        type: "raster",
        tiles: [SATELLITE_TILE_URL],
        tileSize: 256,
        maxzoom: SATELLITE_TILE_MAX_ZOOM,
        attribution: SATELLITE_TILE_ATTRIBUTION,
      });
      map.addLayer({ id: SATELLITE_TILE_LAYER_ID, type: "raster", source: SATELLITE_TILE_SOURCE_ID }, beforeId);
      break;
    case "unset":
      break;
  }
}
